import styles from "./styles.module.css"
import React, {
  useCallback,
  useEffect,
  useMemo,
  memo,
  useState,
  createContext,
  useContext,
  useRef,
} from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { mergeRegister } from "@lexical/utils"
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $nodesOfType,
  CLICK_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
} from "lexical"
import { createPortal } from "react-dom"

import { CommentsPanel } from "./CommentPanel"
import { CommentInputBox } from "./CommentInputBox"
import { apiClient, DataListWrapper, ERROR_MESSAGES, useAppContext } from "@dit/core-frontend"
import { API_ENDPOINTS } from "@src/constants"
import { usePageContext } from "@src/components/contexts/PageContext"
import { $getMarkID, $isMarkNode, $unwrapMarkNode, MarkNode } from "../../nodes/MarkNode"
import { useZoneContext } from "@src/components/contexts/ZoneContext"
import { $findMatchingParent } from "@lexical/utils"
import { debounce } from "lodash"

export const INSERT_INLINE_COMMAND = createCommand("INSERT_INLINE_COMMAND")

const EditorCommentsContext = createContext({
  commentsList: [],
  updateComment: () => {},
  createComment: () => {},
  setIsLoading: () => {},
  disapproveComment: () => {},
  approveComment: () => {},
  isLoading: false,
})

export const useEditorCommentsContext = () => {
  return useContext(EditorCommentsContext)
}

const CommentPluginContent = memo(({ data, getData, pageTitle }) => {
  const { showAlert } = useAppContext()

  const [editor] = useLexicalComposerContext()
  const [commentsList, setCommentsList] = useState(null)
  const { isTextCommentsShow, setIsTextCommentsShow } = useZoneContext()
  const { page } = usePageContext()
  const [isLoading, setIsLoading] = useState(false)
  const [textPositionTop, setTextPositionTop] = useState(0)
  const markNodeMap = useMemo(() => {
    return new Map()
  }, [])
  const [activeAnchorKey, setActiveAnchorKey] = useState()
  const [activeID, setActiveID] = useState("")
  const [isFirstClick, setIsFirstClick] = useState(false)
  const prevActiveID = useRef("")
  const [isScrollToComment, setIsScrollToComment] = useState(false)
  const [indentData, setIndentData] = useState(null)
  const [showCommentInput, setShowCommentInput] = useState(false)
  const [sortedCommentsList, setSortedCommentsList] = useState(null)

  const mainContainer = document.getElementById("mainContainer")
  const editorContainer = document.getElementById("editor_ID")

  // const changesetActiveId = useCallback((value) => {
  //   setActiveID(value)
  // }, [])
  const changeActiveId = useCallback((id, context) => {
    if (prevActiveID.current !== id) {
      setSortedCommentsList(null)
      setActiveID(id)
      // setIsFirstClick(id || false)
    }
  }, [])

  const getComments = useCallback(async (textBlockId) => {
    try {
      return getData(API_ENDPOINTS.textCommentsByBlock, {
        epp: 1000,
        pageId: page.id,
        textBlockId: textBlockId,
        sortDirection: "desc",
      })
    } catch (err) {
      setIsLoading(false)
      showAlert("Ошибка при запросе комментариев")
    }
  }, [])

  const createComment = useCallback(
    async (newComment, replyToId = null, textBlockId, onSuccess) => {
      try {
        await apiClient.post(API_ENDPOINTS.textComments, {
          pageId: page?.id,
          pageVersion: page?.version,
          text: newComment,
          replyToId,
          textBlockId,
        })
        getComments(textBlockId)
      } catch (err) {
        showAlert(err?.data?.errors?.[0]?.message || ERROR_MESSAGES.default)
      }
    },
    [page?.id],
  )

  const updateComment = useCallback(async (commentId, newText, textBlockId, onSuccess) => {
    try {
      await apiClient.patch(`${API_ENDPOINTS.textComments}/${commentId}`, {
        // pageId: page?.id,
        text: newText,
        // replyToId,
        // textBlockId,
      })
      getComments(textBlockId)
    } catch (err) {
      showAlert(err?.data?.errors?.[0]?.message || ERROR_MESSAGES.default)
    }
  }, [])

  const approveComment = useCallback(async (id, textBlockId) => {
    try {
      await apiClient.patch(`${API_ENDPOINTS.textComments}/${id}/approve`)
      getComments(textBlockId)
    } catch (err) {
      showAlert(err?.data?.errors?.[0]?.message || ERROR_MESSAGES.default)
    }
  }, [])
  const disapproveComment = useCallback(async (id, textBlockId) => {
    try {
      await apiClient.patch(`${API_ENDPOINTS.textComments}/${id}/disapprove`)
      getComments(textBlockId)
    } catch (err) {
      showAlert(err?.data?.errors?.[0]?.message || ERROR_MESSAGES.default)
    }
  }, [])

  const deleteComment = useCallback(
    async (id, markId, replyToId) => {
      try {
        await apiClient.delete(`${API_ENDPOINTS.textComments}/${id}`)
        getComments(markId)

        if (!replyToId) {
          const markNodeKeys = markNodeMap.get(markId)
          if ([...markNodeKeys.keys()].length) {
            setTimeout(() => {
              editor.update(
                () => {
                  for (const key of [...markNodeKeys.keys()]) {
                    const node = $getNodeByKey(key)
                    if ($isMarkNode(node)) {
                      node.deleteID(markId)

                      $unwrapMarkNode(node)
                    }
                  }
                },
                { tag: "historic" },
              )
            })
          }

          markNodeMap.delete(markId)
          changeActiveId("", "deleteComment")
          setIsFirstClick(false)
        }
      } catch (err) {
        showAlert(err?.data?.errors?.[0]?.message || ERROR_MESSAGES.default)
      }
    },
    [editor],
  )

  useEffect(() => {
    const updatePosition = () => {
      if (activeID && markNodeMap?.size) {
        const keys = markNodeMap.get(activeID)
        const elem = editor.getElementByKey([...keys.values()][0])
        const rect = elem?.getBoundingClientRect()
        setTextPositionTop(rect?.y || 0)
      }
    }

    // Обновляем позицию при монтировании и при прокрутке
    const debouncedUpdatePosition = debounce(updatePosition, 100)

    debouncedUpdatePosition()
    mainContainer.addEventListener("scroll", debouncedUpdatePosition)

    return () => {
      mainContainer.removeEventListener("scroll", debouncedUpdatePosition)
    }
  }, [activeID, editor, mainContainer, markNodeMap])

  useEffect(() => {
    setCommentsList(data?.itemsList || [])
  }, [data?.itemsList])

  const clickComment = useCallback(() => {
    setIsScrollToComment(true)
  }, [])
  const cancelAddComment = useCallback(() => {
    setShowCommentInput(false)
  }, [])

  useEffect(() => {
    const changedElems = []

    const id = activeID
    const keys = markNodeMap.get(id)

    if (keys !== undefined) {
      for (const key of keys) {
        const elem = editor.getElementByKey(key)
        if (elem !== null) {
          elem.classList.add(`${styles.markSelected}`)
          changedElems.push(elem)
          getComments(id)

          setIsTextCommentsShow(true)

          if (isScrollToComment) {
            elem.scrollIntoView({
              behavior: "instant",
              block: "center",
            })
            setIsScrollToComment(false)
          }
        }
      }
    }

    return () => {
      for (let i = 0; i < changedElems.length; i++) {
        const changedElem = changedElems[i]
        changedElem.classList.remove(`${styles.markSelected}`)
        setIsTextCommentsShow(false)
        setIsFirstClick(false)
      }
    }
  }, [activeID, editor, markNodeMap, setIsTextCommentsShow, isScrollToComment])
  useEffect(() => {
    if (!isTextCommentsShow) {
      const id = activeID
      const keys = markNodeMap.get(id)
      if (keys !== undefined) {
        for (const key of keys) {
          const elem = editor.getElementByKey(key)
          if (elem !== null) {
            elem.classList.remove(`${styles.markSelected}`)
          }
        }
        changeActiveId("", "")
        setIsFirstClick(false)
        setIsScrollToComment(false)
      }
    }
  }, [isTextCommentsShow])

  useEffect(() => {
    const markNodeKeysToIDs = new Map()
    const markNodesIds = []
    editor.update(
      () => {
        const markNodes = $nodesOfType(MarkNode)

        markNodes.forEach((node) => {
          markNodesIds.push(node.__id)
          let markNodeKeys = markNodeMap.get(node.__id) || new Set()
          markNodeKeys.add(node.__key)

          markNodeMap.set(node.__id, markNodeKeys)
        })
        const keys = [...markNodeMap.keys()] || []
        keys.map((key) => {
          if (markNodesIds.indexOf(key) === -1) {
            markNodeMap.delete(key)
          }
          return undefined
        })
      },
      { discrete: true },
    )

    return mergeRegister(
      editor.registerMutationListener(MarkNode, (mutations) => {
        editor.getEditorState().read(() => {
          for (const [key, mutation] of mutations) {
            const node = $getNodeByKey(key)
            let ids = []

            if (mutation === "destroyed") {
              ids = markNodeKeysToIDs.get(key) || ""
            } else if ($isMarkNode(node)) {
              ids = node.getID()
            }

            let markNodeKeys = markNodeMap.get(ids)
            markNodeKeysToIDs.set(key, ids)

            if (mutation === "destroyed") {
              if (markNodeKeys !== undefined) {
                markNodeKeys.delete(key)
                if (markNodeKeys.size === 0) {
                  markNodeMap.delete(ids)
                }
              }
            } else {
              if (markNodeKeys === undefined) {
                const NodeKeysSet = new Set()
                NodeKeysSet.add(node.__key)
                markNodeMap.set(ids, NodeKeysSet)
              }
            }
          }
        })
      }),
      editor.registerUpdateListener(({ editorState, tags }) => {
        editorState.read(() => {
          const selection = $getSelection()
          let hasActiveId = false
          let hasAnchorKey = false

          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode()

            if ($isTextNode(anchorNode)) {
              const commentID = $getMarkID(anchorNode, selection.anchor.offset)
              if (commentID !== null) {
                changeActiveId(commentID || "", "commentID")
                setIsScrollToComment(false)
                hasActiveId = true
              }
              // if (!commentID && selection.anchor.offset !== 0 && !!anchorNode.__prev) {
              //   setActiveID("")
              // }
              if (!selection.isCollapsed()) {
                setActiveAnchorKey(anchorNode.getKey())
                hasAnchorKey = true
              }
            }
          }
          // if (!hasActiveId) {
          //   setActiveID((_activeIds) => (_activeIds ? _activeIds : ""))
          // }
          if (!hasAnchorKey) {
            setActiveAnchorKey(null)
          }
          if (!tags.has("collaboration") && $isRangeSelection(selection)) {
            setShowCommentInput(false)
          }
        })
      }),
      editor.registerCommand(
        CLICK_COMMAND,
        () => {
          const selection = $getSelection()

          const container = $findMatchingParent(selection?.anchor?.getNode(), $isMarkNode)

          if (container?.__id && !isFirstClick) {
            setIsFirstClick(container.__id)
          }
          if (isFirstClick && container?.__id && container?.__id !== isFirstClick) {
            setIsFirstClick(container.__id)
          }
          if (container?.__id && !activeID) {
            changeActiveId(container.__id, "CLICK_COMMAND2")
            setIsFirstClick(container.__id)
          }
          if (activeID && isFirstClick && container?.__id === isFirstClick) {
            setIsFirstClick(false)
            changeActiveId("", "CLICK_COMMAND")
          }
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand(
        INSERT_INLINE_COMMAND,
        (payload) => {
          const domSelection = window.getSelection()
          if (domSelection !== null) {
            domSelection.removeAllRanges()
          }
          setIndentData(payload)
          setShowCommentInput(true)
          return true
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    )
  }, [editor, markNodeMap, activeID, isFirstClick, setIsFirstClick])

  const onAddComment = () => {
    editor.dispatchCommand(INSERT_INLINE_COMMAND, undefined)
  }

  useEffect(() => {
    if (!commentsList?.length && activeID && isFirstClick) {
      showAlert("Комментарии не найдены", { type: "info" })
      setIsTextCommentsShow(false)
      changeActiveId("")
      setIsFirstClick(false)
    }
    if (!commentsList?.length) {
      return
    }
    const sortedComments = Object.values(
      commentsList.reduce((acc, comment) => {
        const isChildComment = !!comment.replyTo
        const mainCommentId = isChildComment ? comment.replyTo.id : comment.id

        let mainComment = acc[mainCommentId]

        if (!mainComment) {
          mainComment = isChildComment
            ? { ...comment.replyTo, reply: [] }
            : { ...comment, reply: [] }
        }

        if (isChildComment) {
          mainComment.reply.push(comment)
        } else {
          mainComment = { ...mainComment, ...comment }
        }

        return {
          ...acc,
          [mainCommentId]: mainComment,
        }
      }, {}),
    )
    setSortedCommentsList(sortedComments[0])
  }, [commentsList])

  useEffect(() => {
    if (!sortedCommentsList?.id && !isLoading && isTextCommentsShow && !markNodeMap.size) {
      setIsTextCommentsShow(false)
      showAlert("Комментарии отсутствуют", { type: "info" })
    }

    if (!activeID && isTextCommentsShow && markNodeMap.size) {
      // setIsFirstClick([...markNodeMap.keys()][0])
      changeActiveId([...markNodeMap.keys()][0], "useEffect")
    }
  }, [isLoading, sortedCommentsList?.id, isTextCommentsShow, isFirstClick])

  useEffect(() => {
    if (isLoading && sortedCommentsList?.id) {
      setIsLoading(false)
    }
  }, [isLoading, sortedCommentsList])

  const shouldRenderCommentsPanel = !isLoading && sortedCommentsList?.id && activeID

  useEffect(() => {
    if (activeID !== prevActiveID.current) {
      prevActiveID.current = activeID
    }
  }, [activeID, editor])

  return (
    <EditorCommentsContext.Provider
      value={{
        createComment,
        updateComment,
        refetchList: getComments,
        approveComment,
        disapproveComment,
        deleteComment,
        isLoading: isLoading,
        setIsLoading: setIsLoading,
      }}
    >
      <>
        {showCommentInput &&
          editorContainer &&
          createPortal(
            <CommentInputBox
              editor={editor}
              pageTitle={pageTitle}
              cancelAddComment={cancelAddComment}
              textPositionTop={textPositionTop}
              indentData={indentData}
            />,
            editorContainer,
          )}

        {shouldRenderCommentsPanel &&
          editorContainer &&
          createPortal(
            <CommentsPanel
              setShowComments={setIsTextCommentsShow}
              comments={sortedCommentsList}
              activeID={activeID}
              setActiveID={changeActiveId}
              clickComment={clickComment}
              markNodeMap={markNodeMap}
              textPositionTop={textPositionTop}
            />,
            editorContainer,
          )}
      </>
    </EditorCommentsContext.Provider>
  )
})
CommentPluginContent.displayName = "CommentPluginComp"

export const CommentPlugin = (props) => (
  <DataListWrapper>
    <CommentPluginContent {...props} />
  </DataListWrapper>
)
